import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { api } from '../api';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8083';

const BADGE_LABELS = {
  streamer: { text: 'STR', color: '#eb0400' },
  mod: { text: 'MOD', color: '#00c400' },
  sub: { text: 'SUB', color: '#D6E9AA' },
};

export default function OBSChat() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const channel = searchParams.get('channel') || '';
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [chatError, setChatError] = useState('');
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    localStorage.setItem('token', token);
    api.me().then(d => setUser(d.user)).catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!channel) return;
    const socket = io(BASE, { transports: ['websocket', 'polling'] });
    socketRef.current = socket;

    socket.on('connect', () => {
      setConnected(true);
      socket.emit('join', { channel, username: '', token });
    });
    socket.on('disconnect', () => setConnected(false));
    socket.on('message', (msg) => setMessages(prev => [...prev.slice(-200), msg]));
    socket.on('error', (data) => { setChatError(data.message || 'Error'); setTimeout(() => setChatError(''), 4000); });
    socket.on('modaction', (data) => {
      if (data.type === 'ban' || data.type === 'timeout') {
        setMessages(prev => prev.map(m => m.username === data.username ? { ...m, removed: true } : m));
      }
    });

    return () => { socket.emit('leave', { channel }); socket.disconnect(); };
  }, [channel, token]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = (e) => {
    e.preventDefault();
    const msg = input.trim();
    if (!msg || !socketRef.current || !user) return;
    socketRef.current.emit('message', { channel, message: msg, token });
    setInput('');
  };

  if (!token || !channel) {
    return (
      <div style={{ padding: 16, color: '#adadb8', fontFamily: "'Nunito', sans-serif", fontSize: '0.82rem', background: '#0e0e10', minHeight: '100vh' }}>
        <style>{`body { background: #0e0e10 !important; margin: 0; }`}</style>
        <p>Set up the chat dock URL:</p>
        <code style={{ display: 'block', marginTop: 8, padding: 8, background: '#18181b', borderRadius: 4, fontSize: '0.7rem', wordBreak: 'break-all' }}>
          {window.location.origin}/obs/chat?token=YOUR_TOKEN&channel=YOUR_USERNAME
        </code>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#0e0e10', fontFamily: "'Nunito', sans-serif", color: '#efeff1' }}>
      <style>{`body { background: #0e0e10 !important; margin: 0; }`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', borderBottom: '1px solid #2a2a2e', flexShrink: 0 }}>
        <span style={{ fontWeight: 800, fontSize: '0.8rem' }}>Chat — {channel}</span>
        <span style={{ fontSize: '0.7rem', fontWeight: 600, color: connected ? '#00c400' : '#adadb8' }}>
          {connected ? '● Live' : '○ ...'}
        </span>
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
        {messages.length === 0 && <div style={{ color: '#adadb8', fontSize: '0.82rem', textAlign: 'center', marginTop: 20 }}>Waiting for messages...</div>}
        {messages.map((msg, i) => (
          <div key={i} style={{ fontSize: '0.82rem', lineHeight: 1.4, wordBreak: 'break-word', opacity: msg.removed ? 0.3 : 1 }}>
            {msg.is_system ? (
              <span style={{ color: '#adadb8', fontStyle: 'italic' }}>{msg.message}</span>
            ) : (
              <>
                {msg.badges?.map(b => {
                  const badge = BADGE_LABELS[b];
                  if (!badge) return null;
                  return <span key={b} style={{ fontSize: '0.6rem', fontWeight: 800, padding: '1px 3px', borderRadius: 2, marginRight: 3, background: badge.color + '33', color: badge.color }}>{badge.text}</span>;
                })}
                <span style={{ fontWeight: 700, marginRight: 6, color: msg.verified ? '#D6E9AA' : '#ffb347' }}>{msg.username}</span>
                <span>{msg.removed ? '<removed>' : msg.message}</span>
              </>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '8px 12px', borderTop: '1px solid #2a2a2e', flexShrink: 0 }}>
        {chatError && <p style={{ color: '#eb0400', fontSize: '0.75rem', marginBottom: 4 }}>{chatError}</p>}
        <form onSubmit={sendMessage} style={{ display: 'flex', gap: 6 }}>
          <input
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder={connected ? 'Send a message...' : 'Connecting...'}
            disabled={!connected || !user}
            maxLength={300}
            style={{ flex: 1, padding: '8px 10px', background: '#18181b', border: '1px solid #2a2a2e', borderRadius: 2, color: '#efeff1', fontSize: '0.82rem', outline: 'none', boxSizing: 'border-box' }}
          />
          <button type="submit" disabled={!connected || !input.trim() || !user}
            style={{ padding: '8px 14px', background: '#D6E9AA', color: '#1a1a1a', border: 'none', borderRadius: 2, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 }}>
            Send
          </button>
        </form>
      </div>
    </div>
  );
}
